/**
 * reportHtml.ts
 *
 * Shared utility for wrapping the frontend-captured report innerHTML into a
 * complete, self-contained HTML document suitable for:
 *   1. Browser printing (iframe-based)
 *   2. Puppeteer PDF generation (POST to backend)
 *
 * This is the single source of truth for report styles, ensuring the printed
 * PDF and browser print both match the on-screen DocumentView exactly.
 */

import { CLINICAL_RICH_TEXT_STYLES } from "./clinicalRichText";
import {
  REPORT_IMAGE_ROLE,
  REPORT_IMAGE_ROLE_ATTRIBUTE,
} from "./reportImageAssets";
import { REPORT_PRINT_PAGE_STYLES } from "./reportPageLayout";

/**
 * Options for the report HTML wrapper.
 */
export interface ReportHtmlOptions {
  /** Display title (used in <title> and browser tab). */
  title?: string;
  /** Facility name shown in the page footer. */
  facilityName?: string;
  /**
   * Raw CSS collected from the parent page's loaded stylesheets (Mantine,
   * app CSS, etc.). When provided it is injected before the report's own
   * styles so the report CSS wins via specificity / !important where needed.
   * Pass the output of collectPageStylesheets() here.
   */
  pageStylesheets?: string;
}

/**
 * Builds a complete HTML document wrapping the captured report body HTML.
 *
 * The styles here intentionally mirror DocumentView.tsx's inline styles and
 * DOCUMENT_STYLES so the PDF output matches what the pathologist sees on screen.
 */
export function buildReportHtml(
  bodyHtml: string,
  options: ReportHtmlOptions = {}
): string {
  const { title = "Pathology Report", pageStylesheets } = options;

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1.0" />
  <title>${escapeHtml(title)}</title>
  ${pageStylesheets ? `<style data-from-page>${pageStylesheets}</style>` : ""}
  <style>
    ${CLINICAL_RICH_TEXT_STYLES}

    /* ── Reset ───────────────────────────────────────────────────────────── */
    *, *::before, *::after {
      box-sizing: border-box;
    }

    /* Mantine inline styles use calc(... * var(--mantine-scale)) for spacing
       props (mt, mb, gap, etc.). Without this variable defined, those calc()
       expressions evaluate to nothing and all spacing collapses. */
    :root {
      --mantine-scale: 1;
    }

    /* ── Base document styles (mirrors DocumentView inline styles) ────────── */
    html, body {
      margin: 0;
      padding: 0;
      background: #fff;
      color: #000;
      font-size: 16px;
    }

    body {
      font-family: 'Times New Roman', Times, serif;
      font-size: 12pt;
      line-height: 1.5;
      padding: 0 20px;
    }

    /* ── Document container (mirrors the 800px max-width in DocumentView) ─── */
    .document-view-container {
      max-width: 800px;
      margin: 0 auto;
      padding: 24px 40px;
      font-family: 'Times New Roman', Times, serif;
      font-size: 12pt;
      line-height: 1.5;
      background: white;
    }

    /* ── Editable section inline-start spacing fix (from DOCUMENT_STYLES) ─── */
    .editable-section-inline-start p:first-child,
    .editable-section-inline-start p:first-of-type {
      margin-top: 0 !important;
      padding-top: 0 !important;
      margin-block-start: 0 !important;
    }

    /* ── Mantine component resets for PDF rendering ───────────────────────── */
    /* Mantine renders interactive elements that should be static in PDF */
    [data-mantine-color-scheme] {
      color-scheme: light;
    }

    /* Remove hover/focus outlines */
    *:focus {
      outline: none !important;
    }

    /* Flatten interactive chips/badges to plain text style */
    [data-variable] {
      display: inline;
      background: none !important;
      border: none !important;
      padding: 0 !important;
      color: inherit !important;
      font: inherit !important;
    }

    /* Remove edit-mode UI elements (pencil icons, toolbars, etc.) */
    .editable-section-toolbar,
    [data-edit-controls] {
      display: none !important;
    }

    /* ── Print-specific ───────────────────────────────────────────────────── */
    @media print {
      html, body {
        padding: 0;
        margin: 0;
        background: #fff;
      }

      .document-view-container {
        padding: 0;
        max-width: 100%;
      }

      ${REPORT_PRINT_PAGE_STYLES}
    }
  </style>
</head>
<body>
  ${bodyHtml}
</body>
</html>`;
}

export async function captureReportHtml(
  reportRoot: ParentNode,
  options: ReportHtmlOptions = {}
): Promise<string> {
  await prepareReportImagesForCapture(findCaptureImages(reportRoot));
  const html = buildReportHtml(readReportPageHtml(reportRoot), options);
  return inlineImagesInHtml(html);
}

function findCaptureImages(reportRoot: ParentNode): HTMLImageElement[] {
  return Array.from(
    reportRoot.querySelectorAll<HTMLImageElement>("img[src]")
  ).filter((image) => image.src && image.src !== window.location.href);
}

function readReportPageHtml(reportRoot: ParentNode): string {
  const pages = Array.from(reportRoot.querySelectorAll<Element>(".pdf-page"));
  if (pages.length === 0) {
    throw new Error("Report content is not ready yet. Please try again.");
  }
  return pages.map((page) => page.outerHTML).join("\n");
}

/**
 * Collects all loaded stylesheets from the current page (Mantine, app CSS,
 * etc.) and returns them as a single CSS string. Cross-origin sheets are
 * silently skipped. Pass the result as `pageStylesheets` to buildReportHtml
 * so Puppeteer has the same class-based styles as the browser preview.
 */
export function collectPageStylesheets(): string {
  const chunks: string[] = [];
  Array.from(document.styleSheets).forEach((sheet) => {
    try {
      Array.from(sheet.cssRules).forEach((rule) => {
        chunks.push(rule.cssText);
      });
    } catch {
      // CORS-protected sheet (third-party fonts) — skip
    }
  });
  return chunks.join("\n");
}

/**
 * Builds Puppeteer-compatible footer HTML for multi-page reports.
 * Shows page number and facility name.
 * Must use inline styles only (no external CSS in Puppeteer footer templates).
 */
export function buildPuppeteerFooterTemplate(facilityName: string): string {
  return `<div style="
    font-family: 'Times New Roman', Times, serif;
    font-size: 9pt;
    color: #555;
    width: 100%;
    padding: 0 0.5in;
    display: flex;
    justify-content: space-between;
    align-items: center;
  ">
    <span>${escapeHtml(facilityName)}</span>
    <span>Page <span class="pageNumber"></span> of <span class="totalPages"></span></span>
  </div>`;
}

/**
 * Inline all `<img src="...">` external URLs in an HTML string as base64 data URIs.
 *
 * This is critical for HTML snapshots stored for delivery queue PDF generation:
 * S3 pre-signed URLs expire within hours, so images must be embedded before
 * the snapshot is stored. For live PDF downloads, images are still valid so this
 * step is optional (but included for correctness).
 *
 * Non-data-URI, non-empty src attributes are fetched and replaced. Images fail
 * closed unless explicit optional branding receives a non-successful response.
 */
interface ExternalReportImage {
  full: string;
  before: string;
  source: string;
  after: string;
  start: number;
  end: number;
}

interface ReportImageReplacement {
  start: number;
  end: number;
  replacement: string;
}

interface EmbeddedReportImage {
  source: string;
  outcome: "embedded";
  dataUri: string;
}

interface UnavailableReportImage {
  source: string;
  outcome: "non-successful";
}

type FetchedReportImage = EmbeddedReportImage | UnavailableReportImage;

const OPTIONAL_BRANDING_FALLBACK = [
  "data:image/svg+xml,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org",
  "%2F2000%2Fsvg%22%20viewBox%3D%220%200%20187.5%2082.38%22%2F%3E",
].join("");
const OPTIONAL_BRANDING_ROLE_PATTERN = new RegExp(
  `(?:^|\\s)${REPORT_IMAGE_ROLE_ATTRIBUTE}="${REPORT_IMAGE_ROLE.optionalBranding}"(?:\\s|>)`,
  "i"
);

export async function inlineImagesInHtml(html: string): Promise<string> {
  const images = findExternalReportImages(html);
  if (images.length === 0) return html;

  const requests = createDistinctImageRequests(images);
  const replacements = await Promise.all(
    images.map((image) => inlineReportImage(image, requests))
  );
  return applyImageReplacements(html, replacements);
}

function findExternalReportImages(html: string): ExternalReportImage[] {
  const imageTagPattern = /<img([^>]*)\bsrc="([^"]+)"([^>]*)>/gi;
  const capturedImages = Array.from(html.matchAll(imageTagPattern)).map(
    (match) => ({
      full: match[0],
      before: match[1],
      source: match[2],
      after: match[3],
      start: match.index ?? 0,
      end: (match.index ?? 0) + match[0].length,
    })
  );
  const decodedSources = decodeDistinctImageSources(capturedImages);
  return capturedImages
    .map((image) => ({
      ...image,
      source: getDecodedImageSource(image.source, decodedSources),
    }))
    .filter(({ source }) => isExternalImageSource(source));
}

function isExternalImageSource(source: string): boolean {
  return Boolean(
    source && !source.startsWith("data:") && !source.startsWith("blob:")
  );
}

function decodeDistinctImageSources(
  images: ExternalReportImage[]
): Map<string, string> {
  const sources = Array.from(new Set(images.map(({ source }) => source)));
  return new Map(
    sources.map((source) => [source, decodeCapturedImageSource(source)])
  );
}

function getDecodedImageSource(
  capturedSource: string,
  decodedSources: Map<string, string>
): string {
  const source = decodedSources.get(capturedSource);
  if (!source) throw new Error("A report image source could not be read.");
  return source;
}

function decodeCapturedImageSource(source: string): string {
  const template = document.createElement("template");
  template.innerHTML = `<img src="${source}">`;
  const image = template.content.querySelector("img");
  const decodedSource = image?.getAttribute("src");
  if (!decodedSource)
    throw new Error("A report image source could not be read.");
  return decodedSource;
}

function createDistinctImageRequests(
  images: ExternalReportImage[]
): Map<string, Promise<FetchedReportImage>> {
  const sources = Array.from(new Set(images.map(({ source }) => source)));
  return new Map(sources.map((source) => [source, fetchReportImage(source)]));
}

async function fetchReportImage(source: string): Promise<FetchedReportImage> {
  const response = await fetch(source);
  if (!response.ok) {
    return { source, outcome: "non-successful" };
  }
  const blob = await response.blob();
  return { source, outcome: "embedded", dataUri: await blobToDataUri(blob) };
}

async function inlineReportImage(
  image: ExternalReportImage,
  requests: Map<string, Promise<FetchedReportImage>>
): Promise<ReportImageReplacement> {
  const request = requests.get(image.source);
  if (!request) throw new Error("A report image request could not be started.");
  let fetchedImage: FetchedReportImage;
  try {
    fetchedImage = await request;
  } catch {
    return replaceNonSuccessfulImage(image);
  }
  if (fetchedImage.outcome === "non-successful") {
    return replaceNonSuccessfulImage(image);
  }
  return replaceImageSource(image, fetchedImage.dataUri);
}

function replaceNonSuccessfulImage(
  image: ExternalReportImage
): ReportImageReplacement {
  if (!OPTIONAL_BRANDING_ROLE_PATTERN.test(image.full)) {
    throw new Error("A report image could not be embedded.");
  }

  return replaceImageSource(
    image,
    OPTIONAL_BRANDING_FALLBACK,
    ' data-report-image-fallback="unavailable"'
  );
}

function replaceImageSource(
  image: ExternalReportImage,
  source: string,
  fallbackAttribute = ""
): ReportImageReplacement {
  const imageStart = `<img${image.before} src="${source}"`;
  return {
    start: image.start,
    end: image.end,
    replacement: `${imageStart}${fallbackAttribute}${image.after}>`,
  };
}

function applyImageReplacements(
  html: string,
  replacements: ReportImageReplacement[]
): string {
  const output: string[] = [];
  let previousEnd = 0;
  replacements.forEach(({ start, end, replacement }) => {
    output.push(html.slice(previousEnd, start), replacement);
    previousEnd = end;
  });
  output.push(html.slice(previousEnd));
  return output.join("");
}

export async function assertReportImageDecoded(
  image: HTMLImageElement
): Promise<void> {
  await image.decode();
  if (!image.complete || image.naturalWidth < 1) {
    throw new Error("A report image could not be decoded.");
  }
}

export async function prepareReportImagesForCapture(
  images: HTMLImageElement[]
): Promise<void> {
  await Promise.all(images.map(prepareReportImageForCapture));
}

async function prepareReportImageForCapture(
  image: HTMLImageElement
): Promise<void> {
  try {
    await assertReportImageDecoded(image);
  } catch (error) {
    if (!isOptionalBrandingImage(image)) throw error;
    image.src = OPTIONAL_BRANDING_FALLBACK;
    image.dataset.reportImageFallback = "unavailable";
  }
}

function isOptionalBrandingImage(image: HTMLImageElement): boolean {
  return (
    image.getAttribute(REPORT_IMAGE_ROLE_ATTRIBUTE) ===
    REPORT_IMAGE_ROLE.optionalBranding
  );
}

function blobToDataUri(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

/**
 * Minimal HTML entity escaping for use in templates.
 */
function escapeHtml(text: string): string {
  return text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}
